import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import {
  EditableComponent,
  EditableComponentProps,
  ResponsiveGrid,
  getChildComponents,
} from '@adobe/aem-react-editable-components';
import { respGridUtil, TabsEditable, useEditorMode } from '@marriott/mi-ui-library';
import { ThemeClusterProps } from './ThemeCluster.types';
import {
  TabComponentContainer,
  Eyebrow,
  Title,
  Subtitle,
  ThemeClusterWrapper,
  THEME_CLUSTER_CONTAINER_CLASS,
  TabContentContainer,
  TabContentWrapper,
} from './ThemeCluster.styles';

// This component renders a single tab's content and is heavily memoized
const TabContent = React.memo<{
  page: object;
  isVisible: boolean;
}>(({ page, isVisible }) => {
  const content = useMemo(() => {
    const cqModel = respGridUtil(page);
    const { cqItems: pageCqItems, cqItemsOrder } = cqModel;
    const children = getChildComponents({ cqItems: pageCqItems, cqItemsOrder });

    // Calculate cumulative card offsets so card numbering is sequential across carousels
    let cardOffset = 0;
    return children.map(child => {
      if (React.isValidElement<{ model?: { totalNumberOfCards?: string[]; cardIndexOffset?: number } }>(child)) {
        const childModel = child.props?.model;
        const totalCards = childModel?.totalNumberOfCards?.length || 0;
        const cloned = React.cloneElement(child, {
          model: { ...childModel, cardIndexOffset: cardOffset },
        });
        cardOffset += totalCards;
        return cloned;
      }
      return child;
    });
  }, [page]);

  if (!content?.length) {
    return null;
  }

  return (
    <TabContentContainer $isVisible={isVisible}>
      {content.map((childComponent: React.ReactNode) => (
        <React.Fragment key={content[0].key}>{childComponent}</React.Fragment>
      ))}
    </TabContentContainer>
  );
});

const CreateCardToPublishWrapper = React.memo<{
  selectedTab: string;
  tabDetails: { nodeName: string; tabName: string }[];
  cqItems: Record<string, object>;
}>(({ cqItems, selectedTab, tabDetails = [] }) => {
  // Pre-process all valid tabs and their content
  const validTabs = useMemo(() => {
    return tabDetails
      .filter(({ nodeName }) => cqItems?.[nodeName])
      .map(({ nodeName }) => ({
        nodeName,
        page: cqItems[nodeName],
      }));
  }, [cqItems, tabDetails]);

  return (
    <TabContentWrapper role="tabpanel">
      {validTabs.map(({ nodeName, page }) => (
        <TabContent key={nodeName} page={page} isVisible={nodeName === selectedTab} />
      ))}
    </TabContentWrapper>
  );
});

export const ThemeClusterComp = (props: ThemeClusterProps) => {
  const { eyebrow, header, pagePath = '', itemPath = '', subHeader, tabDetails, trackingProperties } = props.model;
  const cqItems = respGridUtil(props.model)?.cqItems;

  const tabList = useMemo(() => {
    return (
      (props.isAuthorMode ? tabDetails : tabDetails?.filter(({ nodeName }) => cqItems?.[nodeName]))?.map(tab => ({
        tabValue: tab.nodeName,
        tabTitle: tab.tabName,
        tabSubtitle: '',
      })) || []
    );
  }, [props.isAuthorMode, tabDetails, cqItems]);

  const [selectedTab, setSelectedTab] = useState<string>(() => tabList[0]?.tabValue);
  const onTabSelect = (selectedTabValue: string) => {
    setSelectedTab(selectedTabValue);
  };

  const customTabClickTrackValues = useMemo(() => {
    return tabDetails?.map(
      ({ tabName }, index) => `${trackingProperties?.trackingContentPosition}|Tab${index + 1}-${tabName}|internal`
    );
  }, [tabDetails, trackingProperties]);

  const { isInEditor } = useEditorMode();
  return (
    <ThemeClusterWrapper className={clsx(THEME_CLUSTER_CONTAINER_CLASS, props.className)}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      {header ? <Title>{header}</Title> : null}
      {subHeader ? <Subtitle>{subHeader}</Subtitle> : null}
      <TabComponentContainer>
        <TabsEditable
          clickTrackingLoc={trackingProperties?.trackingContentPosition}
          customTabClickTrackValues={customTabClickTrackValues}
          selectedTab={selectedTab}
          setSelectedTab={(tab: string) => onTabSelect(tab)}
          tabList={tabList}
          ariaRole="link"
        >
          {props.isAuthorMode ? (
            tabDetails?.map(tabDetail => (
              <ResponsiveGrid
                key={tabDetail.nodeName}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                pagePath={pagePath}
                itemPath={`${itemPath}/${tabDetail.nodeName}`}
                columnCount="12"
                gridClassNames={''}
                customClassName={''}
                config={{
                  isEmpty: () => true,
                  resourceType: 'mi-aem-common-spa/components/container',
                }}
                isInEditor={isInEditor}
              />
            ))
          ) : (
            <CreateCardToPublishWrapper
              cqItems={cqItems}
              selectedTab={selectedTab}
              tabDetails={tabList.map(tab => ({ nodeName: tab.tabValue, tabName: tab.tabTitle }))}
            />
          )}
        </TabsEditable>
      </TabComponentContainer>
    </ThemeClusterWrapper>
  );
};

export const ThemeCluster = (
  props: EditableComponentProps &
    ThemeClusterProps & {
      isAuthorMode?: boolean;
    }
) => {
  const { styleclass, ...model } = props?.model || {};

  return (
    <div className={styleclass} data-target-component={model.targetComponentName || null} style={{ clear: 'both' }}>
      {props?.isAuthorMode ? (
        <EditableComponent
          config={{
            emptyLabel: 'ThemeCluster',
            isEmpty: () => false,
            resourceType: 'mi-aem-homepage-spa/components/content/themed-cluster',
          }}
          {...props}
        >
          <ThemeClusterComp {...props} className="container" />
        </EditableComponent>
      ) : (
        <ThemeClusterComp {...props} className="container" />
      )}
    </div>
  );
};
